import React, { FC } from 'react';
import { Col, Dropdown, MenuProps, Row } from 'antd';
import { ReactComponent as Arrow } from 'assets/icons/arrow.svg';
import {
  DownloadFileName,
  DownloadKeys,
  DownloadLabels,
  GraphDropDownTypes,
  SortKeys,
  SortLabels,
} from 'enums/GraphDropdownEnums';
import Styles from './graphDropdown.module.scss';
import { ReactComponent as DownloadIcon } from 'assets/icons/download.svg';
import clsx from 'clsx';
import COButton from 'shared/components/COButton';
import { ProductOverviewEnums } from 'enums/ProductOverviewEnums';
import { downloadProductMap } from 'shared/utils/HelperFunctions';

export interface GraphDropDownProps {
  handleSort?: MenuProps['onClick'];
  // Have implemented a static download function, need to change this when implementing download for Product Dashboard
  handleDownload?: MenuProps['onClick'];
  graphRef?: React.RefObject<HTMLDivElement>;
  downloadCsv?: () => void;
  showSort?: boolean;
  downloadMap?: boolean;
  isSort?: boolean;
  isMainDashboard?: boolean;
  isDownload?: boolean;
  selectedOption?: string;
  downloadFileName?: string;
  setIsDownloading?: React.Dispatch<React.SetStateAction<boolean>>;
}

const GraphDropDown: FC<GraphDropDownProps> = props => {
  const {
    handleSort,
    handleDownload,
    downloadMap,
    graphRef,
    isMainDashboard,
    downloadCsv,
    showSort = true,
    isSort,
    isDownload = true,
    selectedOption,
    downloadFileName,
    setIsDownloading,
  } = props;

  const handleDownloadGraphs: MenuProps['onClick'] = info => {
    const { key } = info;
    if (handleDownload) handleDownload(info);
    else {
      if (key === DownloadKeys.JPG || key === DownloadKeys.PNG) {
        if (!(graphRef?.current && setIsDownloading)) return;
        setIsDownloading(true);
        setTimeout(async () => {
          await downloadProductMap(
            graphRef,
            key,
            downloadFileName || DownloadFileName.CARBON_ONE
          );
          setIsDownloading(false);
        }, 150);
      } else if (key === DownloadKeys.CSV && downloadCsv) {
        downloadCsv();
      }
    }
  };

  const createMenuItems = (
    keys: Record<string, string>,
    labels: Record<string, string>
  ): MenuProps['items'] => {
    return Object.keys(keys).map(key => ({
      key: keys[key as keyof typeof keys],
      label: (
        <p
          className={clsx(
            Styles.option,
            selectedOption === keys[key as keyof typeof keys] &&
              Styles.activeOption
          )}>
          {labels[key as keyof typeof labels]}
        </p>
      ),
    }));
  };

  const sortItems = createMenuItems(SortKeys, SortLabels);
  const downloadItems = createMenuItems(DownloadKeys, DownloadLabels);

  const dropdownData = [
    ...(showSort
      ? [
          {
            key: GraphDropDownTypes.SORT,
            options: sortItems,
            buttonLabel: (
              <div className={clsx(Styles.sortDropdown)}>
                Sort
                <Arrow fontSize={44} />
              </div>
            ),
            onClick: handleSort,
            isRender: true,
          },
        ]
      : []),
    {
      key: GraphDropDownTypes.DOWNLOAD,
      options: downloadItems,
      buttonLabel: downloadMap ? (
        <COButton>
          <DownloadIcon />
          {ProductOverviewEnums.DOWNLOAD}
        </COButton>
      ) : (
        <div className={Styles.downloadDropdownMain}>
          <DownloadIcon />
        </div>
      ),
      onClick: handleDownloadGraphs,
      isRender: isDownload ? true : false,
      isDownload: true,
    },
  ];

  return (
    <Row justify={'end'} className={Styles.rowWrapper} gutter={[20, 20]}>
      {dropdownData.map(item => (
        <Col key={item.key}>
          <Dropdown
            overlayClassName={
              item.isDownload ? '' : Styles.overLayStyles // Conditionally add the overlay class
            }
            getPopupContainer={trigger => trigger.parentNode as HTMLElement}
            menu={{
              items: item.options,
              onClick: item.onClick,
            }}
            trigger={['click']}>
            {item?.isRender ? item.buttonLabel : <></>}
          </Dropdown>
        </Col>
      ))}
    </Row>
  );
};

export default GraphDropDown;
